import { useQuery, useMutation } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { rateEngineApi, RATE_ENGINE_QUERY_KEYS } from '../api';
import type {
  CensoActivityHistoryResponse,
  CensoChatRequest,
  CensoChatResponse,
  CensoHistoryResponse,
  ItemDto,
  ItemRateDto,
  ItemsFilter,
  LibraryHistoryRecord,
  ProjectDto,
  RegionalLibraryItem,
  SaveLibraryRequest,
  SaveLibraryResponse,
  ToggleFavouriteResponse,
} from '../types';

// ─── Query Hooks ──────────────────────────────────────────────────────────────

/** Fetch all rate engine projects (regional libraries and site projects). */
export const useRateEngineProjects = (
  options?: Omit<UseQueryOptions<ProjectDto[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ProjectDto[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.projects(),
    queryFn: rateEngineApi.getProjects,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/** Fetch all global items, optionally filtered by search term or resource group. */
export const useRateEngineItems = (
  filter?: ItemsFilter,
  options?: Omit<UseQueryOptions<ItemDto[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ItemDto[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.items(filter),
    queryFn: () => rateEngineApi.getItems(filter),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/** Fetch all items with their rates for a specific project/library. */
export const useRateEngineProjectItems = (
  projectId: number,
  options?: Omit<UseQueryOptions<ItemRateDto[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ItemRateDto[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.projectItems(projectId),
    queryFn: () => rateEngineApi.getProjectItems(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/** Fetch rates for a single item across all regions for cross-region comparison. */
export const useItemCrossRegionRates = (
  itemCode: string,
  options?: Omit<UseQueryOptions<ItemRateDto[], Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ItemRateDto[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.itemCrossRegion(itemCode),
    queryFn: () => rateEngineApi.getItemCrossRegion(itemCode),
    enabled: !!itemCode,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/**
 * Fetch all active items with nett rates for a regional library.
 * Accepts a region code (e.g. `NW`, `WMID`) or dashed region name (e.g. `north-west`).
 * Items are returned in order: Labour → Plant → Material → Subcontract → Other, then by item code.
 */
export const useRegionalLibraryItems = (
  region: string,
  siteName?: string,
  options?: Omit<UseQueryOptions<RegionalLibraryItem[], Error>, 'queryKey' | 'queryFn'>,
  /** Optional opportunity number(s) disambiguating `siteName` — see `rateEngineApi`. */
  oppNo?: string,
) => {
  return useQuery<RegionalLibraryItem[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.libraryItems(region, siteName, oppNo),
    queryFn: () => rateEngineApi.getRegionalLibraryItems(region, siteName, oppNo),
    enabled: !!region,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    ...options,
  });
};

/**
 * Opportunity number to send with a site library's calls. Sent whenever both a
 * siteName and an oppNo exist (the tender's `opportunityNo`, possibly composite
 * like "21232-21240"); otherwise `undefined` so the request is unchanged. Kept as
 * a hook (always `isReady`) so call sites have one place to change this rule.
 */
export const useSiteOppNo = (_region: string, siteName?: string, oppNo?: string) => ({
  oppNo: siteName && oppNo ? oppNo : undefined,
  isReady: true,
});

// ─── Censo AI Hooks ───────────────────────────────────────────────────────────

/** Mutation — send a natural-language message to Censo; receive an AI reply with a rate delta. */
export const useCensoChat = (
  options?: Omit<UseMutationOptions<CensoChatResponse, Error, CensoChatRequest>, 'mutationFn'>,
) => {
  return useMutation<CensoChatResponse, Error, CensoChatRequest>({
    mutationFn: rateEngineApi.censoChat,
    ...options,
  });
};

/** Fetch the full exchange history for a Censo session (always fresh, no caching). */
export const useCensoHistory = (
  sessionId: string,
  options?: Omit<UseQueryOptions<CensoHistoryResponse, Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CensoHistoryResponse, Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.censoHistory(sessionId),
    queryFn: () => rateEngineApi.getCensoHistory(sessionId),
    enabled: !!sessionId,
    staleTime: 0,
    ...options,
  });
};

/** Activity Assistant's own session history — separate endpoint/shape from `useCensoHistory` above. */
export const useCensoActivityHistory = (
  sessionId: string,
  options?: Omit<UseQueryOptions<CensoActivityHistoryResponse, Error>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<CensoActivityHistoryResponse, Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.censoActivityHistory(sessionId),
    queryFn: () => rateEngineApi.getCensoActivityHistory(sessionId),
    enabled: !!sessionId,
    staleTime: 0,
    ...options,
  });
};

/** Variables for the save library changes mutation. */
export interface SaveLibraryVariables {
  region: string;
  request: SaveLibraryRequest;
  siteName?: string;
  oppNo?: string;
}

/** Fetch the confirmed audit-log of DB changes for a regional or site-specific library. */
export const useLibraryHistory = (
  region: string,
  siteName?: string,
  batchId?: string,
  options?: Omit<UseQueryOptions<LibraryHistoryRecord[], Error>, 'queryKey' | 'queryFn'>,
  oppNo?: string,
) => {
  return useQuery<LibraryHistoryRecord[], Error>({
    queryKey: RATE_ENGINE_QUERY_KEYS.libraryHistory(region, siteName, batchId, oppNo),
    queryFn: () => rateEngineApi.getLibraryHistory(region, siteName, batchId, oppNo),
    enabled: !!region,
    staleTime: 0,
    ...options,
  });
};

/** Variables for the refresh site library mutation. */
export interface RefreshSiteLibraryVariables {
  region: string;
  siteName: string;
  refreshedBy: string;
  oppNo?: string;
}

/** Mutation — reset a site library back to current regional defaults (irreversible). */
export const useRefreshSiteLibrary = (
  options?: Omit<UseMutationOptions<void, Error, RefreshSiteLibraryVariables>, 'mutationFn'>,
) => {
  return useMutation<void, Error, RefreshSiteLibraryVariables>({
    mutationFn: ({ region, siteName, refreshedBy, oppNo }) =>
      rateEngineApi.refreshSiteLibrary(region, siteName, refreshedBy, oppNo),
    ...options,
  });
};

/** Mutation — confirm and persist Censo rate changes to the database. */
export const useSaveLibraryChanges = (
  options?: Omit<UseMutationOptions<SaveLibraryResponse, Error, SaveLibraryVariables>, 'mutationFn'>,
) => {
  return useMutation<SaveLibraryResponse, Error, SaveLibraryVariables>({
    mutationFn: ({ region, request, siteName, oppNo }) =>
      rateEngineApi.saveLibraryChanges(region, request, siteName, oppNo),
    ...options,
  });
};

/** Variables for the toggle favourite mutation. */
export interface ToggleFavouriteVariables {
  region: string;
  code: string;
  isFavourite: boolean;
  siteName?: string;
  oppNo?: string;
}

/** Mutation — mark or unmark a library item as favourite. */
export const useToggleFavourite = (
  options?: Omit<UseMutationOptions<ToggleFavouriteResponse, Error, ToggleFavouriteVariables>, 'mutationFn'>,
) => {
  return useMutation<ToggleFavouriteResponse, Error, ToggleFavouriteVariables>({
    mutationFn: ({ region, code, isFavourite, siteName, oppNo }) =>
      rateEngineApi.toggleFavourite(region, code, isFavourite, siteName, oppNo),
    ...options,
  });
};
